import {
  Label,
  SwitchV2,
  TextV2,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { Box, Paper } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetNotifiablePractitioners,
  useUpdateNotificationSettings,
} from 'api';
import { Practitioner } from 'components/EventForms';
import { ListByFullName } from 'components/List';
import UserAutocomplete from 'components/UserPicker/UserAutocomplete';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Role, User } from 'types/types';
import { isSuperAdmin } from 'utils/role';

type PractitionerForNotification = Practitioner & {
  role?: Role;
};

export const useManageUpdatesModal = (
  evaluationId: string,
  savedPractitionerNotifications?: PractitionerForNotification[],
) => {
  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState(false);
  const [practitionersToNotify, setPractitionersToNotify] = useState<
    PractitionerForNotification[]
  >(savedPractitionerNotifications || []);

  useEffect(() => {
    const isDifferentFromResponse =
      JSON.stringify(savedPractitionerNotifications) !==
      JSON.stringify(practitionersToNotify);

    if (
      savedPractitionerNotifications &&
      (savedPractitionerNotifications?.length || isDifferentFromResponse)
    ) {
      setPractitionersToNotify(savedPractitionerNotifications);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedPractitionerNotifications]);

  const { mutate } = useUpdateNotificationSettings(queryClient);
  const { data, isLoading } = useGetNotifiablePractitioners(evaluationId);

  const onClose = () => {
    setPractitionersToNotify(savedPractitionerNotifications || []);
    setOpenModal(false);
  };

  return {
    openModal,
    setOpenModal,
    openManageUpdateModal: () => setOpenModal(true),
    onManageUpdatesClose: () => onClose(),
    practitionersToNotify,
    setPractitionersToNotify,
    notifiablePractitioners: data?.practitioners,
    isLoadingPractitionerData: isLoading,
    updateNotificationSettings: (evaluatorId: string) =>
      mutate(
        {
          evaluatorId,
          practitionerIds:
            practitionersToNotify?.map((practitioner) => practitioner.id) || [],
        },
        { onSuccess: () => setOpenModal(false) },
      ),
  };
};

type ManageUpdatesModalProps = {
  availablePractitioners: PractitionerForNotification[];
  setPractitionersToNotify: (value: any) => void;
  practitionersToNotify: PractitionerForNotification[];
  user: User;
  savedPractitionerNotifications?: PractitionerForNotification[];
};

export const ManageUpdatesModalContent = ({
  availablePractitioners,
  setPractitionersToNotify,
  practitionersToNotify,
  user,
  savedPractitionerNotifications,
}: ManageUpdatesModalProps) => {
  const { t } = useTranslation();
  const [clearToggle, setClearToggle] = useState(false);

  const getPractitionerOptions = useCallback(() => {
    const allAvailablePractitioners = availablePractitioners.find(
      (practitioner) => practitioner.id === user.id,
    )
      ? availablePractitioners
      : [...availablePractitioners, user];
    const practitionerToNotifyIdList = practitionersToNotify.map(
      ({ id }) => id,
    );
    return allAvailablePractitioners.filter(
      (practitioner) => !practitionerToNotifyIdList.includes(practitioner.id),
    );
  }, [practitionersToNotify, availablePractitioners, user]);

  const showRemove = (practitioner: PractitionerForNotification) =>
    isSuperAdmin(user.role) || practitioner.role !== 'SUPER_ADMIN';

  const onRemove = (practitioner: PractitionerForNotification) => {
    setPractitionersToNotify(
      practitionersToNotify.filter((a) => a.id !== practitioner.id),
    );
  };

  const getCanToggleOff = () => {
    if (!isSuperAdmin(user.role)) {
      return practitionersToNotify.some(
        (practitioner) => practitioner.role === 'SUPER_ADMIN',
      );
    }
    return false;
  };

  return (
    <>
      <TextV2 color="textSecondary">
        {t('ui.event-management.evaluator-links.notifications.manage.info')}
      </TextV2>
      <Paper
        color="secondary"
        component={'div'}
        variant="outlined"
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.grey[200],
          padding: '8px 8px 4px 8px',
          alignContent: 'baseline',
          marginTop: '8px',
        })}
      >
        <TextV2 variant="bodyBold">
          {t(
            'ui.event-management.evaluator-links.notifications.manage.toggle-label',
          )}
        </TextV2>
        <Box
          sx={{
            mt: '4px',
          }}
        >
          <SwitchV2
            name="UpdateNotificationSettingsSwitch"
            id="toggleSwitch"
            value={!!practitionersToNotify?.length}
            checked={!!practitionersToNotify?.length}
            onChange={(value) => {
              if (value && savedPractitionerNotifications?.length) {
                setPractitionersToNotify(savedPractitionerNotifications);
              } else {
                setPractitionersToNotify(value ? [user] : []);
              }
            }}
            labelLeft={t(
              'ui.event-management.evaluator-links.notifications.state.off',
            )}
            labelRight={t(
              'ui.event-management.evaluator-links.notifications.state.on',
            )}
            disabled={getCanToggleOff()}
          />
        </Box>
      </Paper>

      {practitionersToNotify.length ? (
        <ListByFullName
          items={practitionersToNotify || []}
          itemSpacing={spacingSizeMap.XS}
          showRemove={showRemove}
          onRemove={onRemove}
        />
      ) : null}

      {practitionersToNotify.length ? (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <Label>
            {t(
              'ui.event-management.evaluator-links.notifications.manage.select-user-field-label',
            )}
          </Label>
          <UserAutocomplete
            options={getPractitionerOptions()}
            onSelect={(value) => {
              setPractitionersToNotify([...practitionersToNotify, value]);
              setClearToggle(!clearToggle);
            }}
            selectedValue={''}
            loading={false}
            placeholder={t(
              'ui.event-management.evaluator-links.notifications.manage.select-user-field.placeholder',
            )}
            clearToggle={clearToggle}
          />
        </Box>
      ) : null}
    </>
  );
};
